<script setup>
import { ref, computed, watch, onMounted } from "vue";
import {
  getClientes,
  crearCliente,
  actualizarCliente,
  eliminarCliente,
  exportarClientes,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { usePaginacionClientesStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";
import { ESTADOS_MEXICO } from "@/utils/estadosMexico";
import { LADAS, LADA_LONGITUD, soloDigitos } from "@/utils/ladas";

// ── Store compartido ────────────────────────────────────────
const paginacionStore = usePaginacionClientesStore();

// ── Estado ─────────────────────────────────────────────────
const clientes = ref([]);
const filtro = ref("");
const filtroTipoSet = ref(new Set());
const loading = ref(false);
const paginaActual = ref(1);
const guardando = ref(false);
const editando = ref(false);
const modalElement = ref(null);
const form = ref({
  id: null,
  numero_cliente: "",
  nombre: "",
  telefono: "",
  prefijo: "+52",
  tipo_cliente: "NACIONAL",
  estado: "",
  pais: "",
});

// ── Ordenamiento ───────────────────────────────────────────
const sortKey = ref("numero_cliente");
const sortDir = ref("asc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

let modalInstance = null;

// ── Teléfono controlado: solo dígitos, longitud según lada ──
const maxLongitud = computed(() => LADA_LONGITUD[form.value.prefijo] || 10);

const telefonoValido = computed(() => {
  const n = form.value.telefono;
  if (!n) return true; // el teléfono es opcional
  return n.length === maxLongitud.value;
});

// v-model del input: filtra en cada tecleo/pegado — nunca permite
// espacios, guiones ni letras, y trunca a la longitud de la lada activa.
const telefonoInput = computed({
  get: () => form.value.telefono,
  set: (val) => {
    form.value.telefono = soloDigitos(val).slice(0, maxLongitud.value);
  },
});

// v-model del select de lada: al cambiarla manualmente, re-trunca el
// número ya escrito a la nueva longitud. No se usa un watch(prefijo)
// porque abrirModal() reasigna form.value completo al cargar un cliente
// existente, y un watch también dispararía ahí — truncando en silencio
// números legados largos antes de que el usuario los vea para corregirlos.
const prefijoInput = computed({
  get: () => form.value.prefijo,
  set: (val) => {
    form.value.prefijo = val;
    form.value.telefono = soloDigitos(form.value.telefono).slice(
      0,
      LADA_LONGITUD[val] || 10
    );
  },
});

// ── Tipo de cliente ──────────────────────────────────────────
const TIPOS_CLIENTE = [
  { value: "NACIONAL", label: "Nacional" },
  { value: "INTERNACIONAL", label: "Internacional" },
];

function getTipoLabel(value) {
  return TIPOS_CLIENTE.find((t) => t.value === value)?.label || "—";
}

function getEstadoLabel(value) {
  return ESTADOS_MEXICO.find((e) => e.value === value)?.label || value || "—";
}

// Texto a mostrar en tabla/cards para Estado o País según tipo_cliente
function getUbicacionTexto(cliente) {
  if (cliente.tipo_cliente === "INTERNACIONAL") return cliente.pais || "—";
  return getEstadoLabel(cliente.estado);
}

// ── Dropdown personalizado: Tipo de cliente ─────────────────
const tipoDropdownAbierto = ref(false);

function elegirTipo(t) {
  form.value.tipo_cliente = t.value;
  tipoDropdownAbierto.value = false;
}

function cerrarTipoDropdown() {
  tipoDropdownAbierto.value = false;
}

// ── Dropdown personalizado: Estado ───────────────────────────
const estadoDropdownAbierto = ref(false);
const estadoBusqueda = ref("");

const estadosFiltrados = computed(() => {
  const s = estadoBusqueda.value.trim().toLowerCase();
  if (!s) return ESTADOS_MEXICO;
  return ESTADOS_MEXICO.filter(
    (e) => e.label.toLowerCase().includes(s) || e.value.toLowerCase().includes(s)
  );
});

function elegirEstado(e) {
  form.value.estado = e.value;
  estadoDropdownAbierto.value = false;
  estadoBusqueda.value = "";
}

function cerrarEstadoDropdown() {
  estadoDropdownAbierto.value = false;
  estadoBusqueda.value = "";
}

// Al cambiar el tipo de cliente en el modal, limpiamos el campo que ya no aplica
watch(
  () => form.value.tipo_cliente,
  (nuevoTipo) => {
    if (nuevoTipo === "NACIONAL") {
      form.value.pais = "";
    } else {
      form.value.estado = "";
    }
  }
);

// Resetear página al filtrar
watch([filtro, filtroTipoSet], () => {
  paginaActual.value = 1;
});

// ── Computed ───────────────────────────────────────────────
const clientesFiltrados = computed(() => {
  const s = filtro.value.toLowerCase();
  return clientes.value.filter(
    (c) =>
      c.numero_cliente.toLowerCase().includes(s) ||
      c.nombre.toLowerCase().includes(s) ||
      (c.telefono || "").toLowerCase().includes(s) ||
      getTipoLabel(c.tipo_cliente).toLowerCase().includes(s) ||
      getUbicacionTexto(c).toLowerCase().includes(s)
  );
});

// Universo con la etiqueta legible del tipo, para que el embudo de la
// columna Tipo compare/muestre "Nacional"/"Internacional" en vez del
// código crudo. Se agrega antes del filtro para que tipo_label quede
// disponible también al ordenar.
const clientesParaFiltro = computed(() =>
  clientesFiltrados.value.map((c) => ({ ...c, tipo_label: getTipoLabel(c.tipo_cliente) }))
);

const clientesFiltradosPorTipo = computed(() =>
  clientesParaFiltro.value.filter(
    (c) => !filtroTipoSet.value.size || filtroTipoSet.value.has(c.tipo_label)
  )
);

const clientesOrdenados = computed(() => {
  return [...clientesFiltradosPorTipo.value].sort((a, b) => {
    let valA = a[sortKey.value] ?? "";
    let valB = b[sortKey.value] ?? "";
    const cmp = String(valA).localeCompare(String(valB), "es", { sensitivity: "base" });
    return sortDir.value === "asc" ? cmp : -cmp;
  });
});

const porPagina = computed(() => paginacionStore.porPagina);

const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(clientesOrdenados.value.length / porPagina.value))
);

const clientesPaginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return clientesOrdenados.value.slice(inicio, inicio + porPagina.value);
});

const rangoInicio = computed(() =>
  clientesOrdenados.value.length === 0
    ? 0
    : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, clientesOrdenados.value.length)
);

const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

// ── Métodos ────────────────────────────────────────────────
function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

async function cargarClientes() {
  loading.value = true;
  try {
    clientes.value = await getClientes();
  } catch {
    Swal.fire("Error", "No se pudieron obtener los clientes", "error");
  } finally {
    loading.value = false;
  }
}

function abrirModal(cliente = null) {
  if (cliente) {
    editando.value = true;
    // Registros viejos pueden traer espacios, guiones o texto suelto —
    // se limpia a puros dígitos y se detecta la lada por prefijo numérico,
    // sin asumir un separador fijo como " ".
    const digitos = soloDigitos(cliente.telefono);
    const ladaEncontrada = [...LADAS]
      .sort((a, b) => b.codigo.length - a.codigo.length)
      .find((l) => digitos.startsWith(l.codigo.slice(1)));
    const prefijoGuardado = ladaEncontrada ? ladaEncontrada.codigo : "+52";
    const numeroSolo = ladaEncontrada
      ? digitos.slice(prefijoGuardado.length - 1)
      : digitos;
    form.value = {
      id: cliente.id,
      numero_cliente: cliente.numero_cliente,
      nombre: cliente.nombre,
      telefono: numeroSolo,
      prefijo: prefijoGuardado,
      tipo_cliente: cliente.tipo_cliente || "NACIONAL",
      estado: cliente.estado || "",
      pais: cliente.pais || "",
    };
  } else {
    editando.value = false;
    form.value = {
      id: null,
      numero_cliente: "",
      nombre: "",
      telefono: "",
      prefijo: "+52",
      tipo_cliente: "NACIONAL",
      estado: "",
      pais: "",
    };
  }
  tipoDropdownAbierto.value = false;
  estadoDropdownAbierto.value = false;
  estadoBusqueda.value = "";
  modalInstance.show();
}

async function guardarCliente() {
  if (!telefonoValido.value) {
    Swal.fire(
      "Teléfono incompleto",
      `El teléfono para ${form.value.prefijo} debe tener ${maxLongitud.value} dígitos (tiene ${form.value.telefono.length}).`,
      "warning"
    );
    return;
  }
  guardando.value = true;
  try {
    const telFull = form.value.telefono.trim()
      ? `${form.value.prefijo} ${form.value.telefono.trim()}`
      : null;
    const esNacional = form.value.tipo_cliente === "NACIONAL";
    const payload = {
      numero_cliente: form.value.numero_cliente,
      nombre: form.value.nombre,
      telefono: telFull,
      tipo_cliente: form.value.tipo_cliente,
      estado: esNacional ? form.value.estado : null,
      pais: esNacional ? null : form.value.pais.trim(),
    };
    if (editando.value) {
      await actualizarCliente(form.value.id, payload);
    } else {
      await crearCliente(payload);
    }
    await cargarClientes();
    modalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: `Cliente ${editando.value ? "actualizado" : "registrado"} correctamente`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo guardar el cliente", "error");
  } finally {
    guardando.value = false;
  }
}

async function confirmarBorrado(cliente) {
  const res = await Swal.fire({
    title: `¿Desactivar a ${cliente.nombre}?`,
    text: "El cliente será marcado como inactivo.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (res.isConfirmed) {
    try {
      await eliminarCliente(cliente.id);
      await cargarClientes();
      Swal.fire({
        icon: "success",
        title: "Listo",
        text: "Cliente desactivado",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("Error", "No se pudo desactivar el cliente", "error");
    }
  }
}

// WhatsApp — el teléfono ya incluye la lada, quitar todo lo no numérico
function abrirWhatsapp(telefono) {
  const num = telefono.replace(/\D/g, "");
  window.open(`https://wa.me/${num}`, "_blank");
}

// ── Exportar Excel ────────────────────────────────────────
async function exportarExcel() {
  try {
    await exportarClientes();
  } catch {
    Swal.fire("Error", "No se pudo generar el archivo Excel.", "error");
  }
}

onMounted(() => {
  cargarClientes();
  modalInstance = new Modal(modalElement.value);
});

const vClickOutside = {
  mounted(el, b) {
    el._co = (e) => {
      if (!el.contains(e.target)) b.value(e);
    };
    document.addEventListener("click", el._co);
  },
  unmounted(el) {
    document.removeEventListener("click", el._co);
  },
};
</script>

<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Clientes"
      v-model="filtro"
      searchPlaceholder="Buscar por número, nombre o teléfono..."
    >
      <template #actions>
        <button class="btn btn-exportar btn-header" @click="exportarExcel">
          <i class="bi bi-file-earmark-excel"></i>
          <span class="btn-header-texto ms-2">Exportar Excel</span>
        </button>
        <button class="btn btn-nuevo btn-header" @click="abrirModal(null)">
          <i class="bi bi-person-plus-fill"></i>
          <span class="btn-header-texto ms-2">Nuevo cliente</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- TABLA ESCRITORIO -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>

      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4">
              <ColumnFilterMenu
                field="numero_cliente"
                label="Número de cliente"
                :filterable="false"
                :items="clientesFiltrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="nombre"
                label="Nombre"
                :filterable="false"
                :items="clientesFiltrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="tipo_label"
                label="Tipo"
                :items="clientesParaFiltro"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                v-model="filtroTipoSet"
                @update:sort="onSort"
              />
            </th>
            <th>Estado / País</th>
            <th>
              <ColumnFilterMenu
                field="telefono"
                label="Teléfono / Celular"
                :filterable="false"
                :items="clientesFiltrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cliente in clientesPaginados" :key="cliente.id">
            <td class="ps-4">
              <span class="badge-molde">{{ cliente.numero_cliente }}</span>
            </td>
            <td class="fw-bold text-cafe">{{ cliente.nombre }}</td>
            <td>
              <span
                :class="[
                  'badge-tipo-cliente',
                  cliente.tipo_cliente === 'INTERNACIONAL' ? 'internacional' : 'nacional',
                ]"
              >
                {{ getTipoLabel(cliente.tipo_cliente) }}
              </span>
            </td>
            <td>{{ getUbicacionTexto(cliente) }}</td>
            <td>
              <div v-if="cliente.telefono" class="tel-cell">
                <span class="tel-numero">{{ cliente.telefono }}</span>
                <button
                  class="btn-wa"
                  title="Abrir WhatsApp"
                  @click="abrirWhatsapp(cliente.telefono)"
                >
                  <i class="bi bi-whatsapp"></i>
                </button>
              </div>
              <span v-else class="sin-tel">Sin teléfono</span>
            </td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button class="btn-circular" @click="abrirModal(cliente)" title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarBorrado(cliente)"
                  title="Desactivar"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="clientesPaginados.length === 0 && !loading">
            <td colspan="6" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron clientes
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación escritorio -->
      <div class="paginacion-wrapper">
        <div class="pag-izq">
          <small class="pag-info">
            {{ rangoInicio }}–{{ rangoFin }} de {{ clientesOrdenados.length }} clientes
          </small>
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>

        <div class="pag-der">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="paginaActual = 1"
            title="Primera"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <button class="btn-pag" :disabled="paginaActual === 1" @click="paginaActual--">
            <i class="bi bi-chevron-left"></i>
          </button>

          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['btn-pag-num', { activo: p === paginaActual }]"
              @click="paginaActual = p"
            >
              {{ p }}
            </button>
          </template>

          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual = totalPaginas"
            title="Última"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- CARDS MÓVIL -->
    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div
        v-else
        v-for="cliente in clientesPaginados"
        :key="'mob-' + cliente.id"
        class="divisan-mobile-card mb-3 p-3 shadow-sm"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="fw-bold text-cafe card-name">{{ cliente.nombre }}</span>
          <span :class="['badge-estado', cliente.activo ? 'activo' : 'inactivo']">
            {{ cliente.activo ? "Activo" : "Inactivo" }}
          </span>
        </div>
        <div class="mb-2">
          <label class="small text-muted d-block">Número de cliente</label>
          <span class="badge-molde">{{ cliente.numero_cliente }}</span>
        </div>
        <div class="mb-2 d-flex gap-4">
          <div>
            <label class="small text-muted d-block">Tipo</label>
            <span
              :class="[
                'badge-tipo-cliente',
                cliente.tipo_cliente === 'INTERNACIONAL' ? 'internacional' : 'nacional',
              ]"
            >
              {{ getTipoLabel(cliente.tipo_cliente) }}
            </span>
          </div>
          <div>
            <label class="small text-muted d-block">
              {{ cliente.tipo_cliente === "INTERNACIONAL" ? "País" : "Estado" }}
            </label>
            <span class="text-cafe fw-bold">{{ getUbicacionTexto(cliente) }}</span>
          </div>
        </div>
        <div class="mb-3">
          <label class="small text-muted d-block">Teléfono</label>
          <div v-if="cliente.telefono" class="tel-cell">
            <span class="tel-numero">{{ cliente.telefono }}</span>
            <button
              class="btn-wa"
              @click="abrirWhatsapp(cliente.telefono)"
              title="WhatsApp"
            >
              <i class="bi bi-whatsapp"></i>
            </button>
          </div>
          <span v-else class="sin-tel">Sin teléfono</span>
        </div>
        <div class="d-flex justify-content-end gap-3 pt-2 border-top">
          <i class="bi bi-pencil-square text-verde fs-5" @click="abrirModal(cliente)"></i>
          <i
            class="bi bi-trash3-fill text-danger fs-5"
            @click="confirmarBorrado(cliente)"
          ></i>
        </div>
      </div>

      <!-- Paginación móvil -->
      <div class="paginacion-mobile">
        <div class="pag-mobile-top">
          <small class="pag-info">
            {{ rangoInicio }}–{{ rangoFin }} de {{ clientesOrdenados.length }} clientes
          </small>
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-mobile-nav">
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === 1"
            @click="paginaActual--"
          >
            <i class="bi bi-chevron-left"></i> Anterior
          </button>
          <small class="text-verde fw-bold"
            >{{ paginaActual }} / {{ totalPaginas }}</small
          >
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            Siguiente <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL CREAR / EDITAR -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalElement">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              {{ editando ? "Editar cliente" : "Nuevo cliente" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="guardarCliente">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label label-divisan">Número de cliente</label>
                <input
                  v-model="form.numero_cliente"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Ej. CLI-001"
                />
              </div>
              <div class="mb-3">
                <label class="form-label label-divisan">Nombre</label>
                <input
                  v-model="form.nombre"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Nombre completo o razón social"
                />
              </div>

              <!-- Tipo de cliente -->
              <div class="mb-3" v-click-outside="cerrarTipoDropdown">
                <label class="form-label label-divisan">Tipo de cliente</label>
                <div class="custom-select-wrapper">
                  <input
                    type="text"
                    class="form-control divisan-input-modal custom-select-input"
                    :value="getTipoLabel(form.tipo_cliente)"
                    readonly
                    style="cursor: pointer; background: white"
                    @mousedown.prevent="tipoDropdownAbierto = !tipoDropdownAbierto"
                  />
                  <i
                    class="bi custom-select-arrow"
                    :class="tipoDropdownAbierto ? 'bi-chevron-up' : 'bi-chevron-down'"
                    @mousedown.prevent="tipoDropdownAbierto = !tipoDropdownAbierto"
                    style="pointer-events: auto; cursor: pointer"
                  ></i>
                  <div v-if="tipoDropdownAbierto" class="custom-dropdown dropdown-portal">
                    <div
                      v-for="t in TIPOS_CLIENTE"
                      :key="t.value"
                      class="custom-dropdown-item"
                      :class="{ selected: form.tipo_cliente === t.value }"
                      @mousedown.prevent="elegirTipo(t)"
                    >
                      <span class="item-nombre">{{ t.label }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Estado (solo nacional) -->
              <div
                class="mb-3"
                v-if="form.tipo_cliente === 'NACIONAL'"
                v-click-outside="cerrarEstadoDropdown"
              >
                <label class="form-label label-divisan">Estado</label>
                <div class="custom-select-wrapper">
                  <input
                    v-model="estadoBusqueda"
                    type="text"
                    class="form-control divisan-input-modal custom-select-input"
                    :placeholder="
                      form.estado
                        ? getEstadoLabel(form.estado)
                        : 'Selecciona un estado...'
                    "
                    @focus="estadoDropdownAbierto = true"
                    @input="estadoDropdownAbierto = true"
                  />
                  <i
                    class="bi custom-select-arrow"
                    :class="estadoDropdownAbierto ? 'bi-chevron-up' : 'bi-chevron-down'"
                    @mousedown.prevent="estadoDropdownAbierto = !estadoDropdownAbierto"
                    style="pointer-events: auto; cursor: pointer"
                  ></i>
                  <div
                    v-if="estadoDropdownAbierto"
                    class="custom-dropdown dropdown-portal"
                  >
                    <div
                      v-if="estadosFiltrados.length === 0"
                      class="custom-dropdown-empty"
                    >
                      Sin resultados
                    </div>
                    <div
                      v-for="e in estadosFiltrados"
                      :key="e.value"
                      class="custom-dropdown-item"
                      :class="{ selected: form.estado === e.value }"
                      @mousedown.prevent="elegirEstado(e)"
                    >
                      <span class="item-codigo">{{ e.value }}</span>
                      <span class="item-nombre">{{ e.label }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- País (solo internacional) -->
              <div class="mb-3" v-else>
                <label class="form-label label-divisan">País</label>
                <input
                  v-model="form.pais"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Ej. Estados Unidos"
                />
              </div>

              <!-- Teléfono con select de lada -->
              <div class="mb-1">
                <label class="form-label label-divisan">
                  Teléfono / Celular
                  <span class="label-opcional">(opcional)</span>
                </label>
                <div class="tel-input-wrap">
                  <select v-model="prefijoInput" class="lada-select">
                    <option v-for="l in LADAS" :key="l.codigo" :value="l.codigo">
                      {{ l.bandera }} {{ l.codigo }}
                    </option>
                  </select>
                  <input
                    v-model="telefonoInput"
                    type="tel"
                    inputmode="numeric"
                    class="form-control divisan-input-modal tel-input"
                    :placeholder="'8448935204'.slice(0, maxLongitud)"
                    :maxlength="maxLongitud"
                  />
                </div>
                <small v-if="form.telefono && !telefonoValido" class="text-danger d-block mt-1">
                  <i class="bi bi-exclamation-triangle-fill me-1"></i>
                  Debe tener {{ maxLongitud }} dígitos — tiene {{ form.telefono.length }}.
                </small>
                <small v-else class="text-cafe opacity-50 d-block mt-1">
                  <i class="bi bi-whatsapp me-1 text-verde"></i>
                  Solo dígitos, sin espacios ni guiones ({{ form.telefono.length }}/{{ maxLongitud }}). Se
                  usará para notificaciones de pedidos y envío de documentos por WhatsApp.
                </small>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="submit"
                class="btn btn-nuevo w-100 py-2"
                :disabled="guardando || !telefonoValido"
              >
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar cliente" : "Guardar cliente" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: #703808;
  color: var(--d-cream);
}

.divisan-card {
  background-color: var(--d-cream);
  border-radius: 12px;
  border-left: 5px solid var(--d-green);
}
.divisan-input {
  background-color: white !important;
  border: 1px solid var(--d-green);
  color: var(--d-brown) !important;
  border-radius: 8px;
}
.search-wrapper {
  position: relative;
}
.search-wrapper i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  z-index: 5;
}
.search-wrapper input {
  padding-left: 35px;
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-2);
  overflow: hidden;
}

.table-divisan th {
  padding: 15px;
  font-weight: 800;
  text-transform: capitalize;
  border: none;
  white-space: nowrap;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 12px 15px;
}
.table-divisan tbody tr:hover td {
  background-color: rgba(96, 108, 56, 0.05);
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 1px solid var(--d-border);
}

.badge-molde {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
  padding: 4px 10px;
  border-radius: 6px;
  font-family: var(--d-font-mono);
  font-size: 0.85rem;
  font-weight: 600;
}
.badge-tipo-cliente {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
}
.badge-tipo-cliente.nacional {
  background-color: rgba(96, 108, 56, 0.15);
  color: var(--d-green);
}
.badge-tipo-cliente.internacional {
  background-color: rgba(64, 34, 8, 0.12);
  color: var(--d-brown);
}
.badge-estado {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
}
.activo {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.inactivo {
  background-color: var(--d-brown);
  color: var(--d-cream);
}

/* ── Teléfono en tabla ── */
.tel-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.tel-numero {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--d-brown);
  font-family: var(--d-font-mono);
}
.btn-wa {
  background: #25d366;
  color: white;
  border: none;
  border-radius: 6px;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}
.btn-wa:hover {
  background: #128c4f;
}
.sin-tel {
  font-size: 0.8rem;
  color: var(--d-brown);
  opacity: 0.35;
  font-style: italic;
}

/* ── Campo teléfono en modal ── */
.label-opcional {
  font-size: 0.72rem;
  font-weight: 400;
  color: var(--d-brown);
  opacity: 0.5;
  margin-left: 4px;
}
.tel-input-wrap {
  display: flex;
  align-items: stretch;
  border: 1px solid var(--d-green);
  border-radius: 8px;
  overflow: hidden;
  background: white;
}

/* Select de lada con flecha SVG personalizada */
.lada-select {
  appearance: none;
  -webkit-appearance: none;
  background: rgba(96, 108, 56, 0.07)
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath fill='%23606c38' d='M0 0l4 5 4-5z'/%3E%3C/svg%3E")
    no-repeat right 7px center;
  border: none;
  border-right: 1px solid rgba(96, 108, 56, 0.25);
  padding: 0 22px 0 8px;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--d-green);
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
  min-width: 74px;
}
.lada-select:focus {
  outline: none;
}

.tel-input {
  border: none !important;
  border-radius: 0 !important;
  flex: 1;
  outline: none;
  box-shadow: none !important;
}
.tel-input:focus {
  border: none !important;
  box-shadow: none !important;
}

.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-green);
}
.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
}
.btn-circular:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}

.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}

/* ── Dropdown personalizado (mismo estilo que el selector de artículos
   en PedidoPaso2.vue) — usado en Tipo de cliente y Estado ────────── */
.custom-select-wrapper {
  position: relative;
}
.custom-select-input {
  padding-right: 2.2rem !important;
}
.custom-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  font-size: 0.8rem;
}
.custom-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 9999;
  background: white;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(40, 54, 24, 0.18);
  /* 5 renglones visibles (38px c/u); de ahí en adelante, scroll */
  max-height: 190px;
  overflow-y: auto;
}
.custom-dropdown::-webkit-scrollbar {
  width: 7px;
}
.custom-dropdown::-webkit-scrollbar-track {
  background: transparent;
}
.custom-dropdown::-webkit-scrollbar-thumb {
  background-color: rgba(96, 108, 56, 0.4);
  border-radius: 10px;
}
.custom-dropdown::-webkit-scrollbar-thumb:hover {
  background-color: rgba(96, 108, 56, 0.65);
}
.custom-dropdown {
  scrollbar-width: thin;
  scrollbar-color: rgba(96, 108, 56, 0.4) transparent;
}
.custom-dropdown-item {
  height: 38px;
  padding: 0 12px;
  cursor: pointer;
  display: flex;
  gap: 8px;
  align-items: center;
  border-bottom: 1px solid rgba(96, 108, 56, 0.08);
  transition: background 0.12s;
}
.custom-dropdown-item:last-child {
  border-bottom: none;
}
.custom-dropdown-item:hover,
.custom-dropdown-item.selected {
  background: rgba(96, 108, 56, 0.12);
}
.custom-dropdown-empty {
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(64, 34, 8, 0.4);
  font-size: 0.85rem;
  text-align: center;
}
.item-codigo {
  font-family: var(--d-font-mono);
  font-size: 0.8rem;
  font-weight: 700;
  background: var(--d-green-dark);
  color: var(--d-cream);
  border-radius: 4px;
  padding: 1px 6px;
  flex-shrink: 0;
}
.item-nombre {
  font-size: 0.85rem;
  color: var(--d-brown);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Paginación escritorio ── */
.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}

/* Select por página */
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  min-width: 58px;
}
.por-pagina-select:focus,
.por-pagina-select:hover {
  border-color: var(--d-brown);
}

/* Botones de navegación */
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}

.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}

.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}

/* ── Paginación móvil ── */
.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}

.btn-nuevo-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
.btn-exportar {
  background-color: var(--d-green);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-exportar:hover:not(:disabled) {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.card-name {
  font-size: 0.92rem;
}

@media (max-width: 991.98px) {
  .btn-nuevo-texto,
  .btn-header-texto {
    display: none;
  }
  .btn-nuevo-header,
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

@media (max-width: 768px) {
  .paginacion-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  /* Empuja el modal hacia abajo para que no quede oculto bajo el navbar
     fijo en mobile. Ajusta el valor de margin-top a la altura real
     de tu navbar (parece ser ~60-70px por el logo + botón de menú). */
  .modal-dialog-centered {
    margin-top: 70px;
    min-height: calc(100% - 70px);
  }
}
</style>
